import { useQueryClient } from '@tanstack/react-query';
import { components } from 'src/generated/v1';
import { $publicApi } from './api';

/**
 * Public end-customer endpoints — they must go through `$publicApi`, i.e. the
 * client *without* the auth middleware.
 *
 * Backend-side every route below hangs off `clientportal_tracking_service`
 * (`df_clientportal_api/api/sale_order/routes/customer_tracking.py`), not the
 * JWT environment: the tracking key in the path *is* the credential. The
 * consumers are `/tracking/auth/:orderNumber`, `/tracking/details/:trackingKey`
 * and `/return/:orderId` — pages an end customer of a merchant opens from a
 * shipping mail, who never has a portal token.
 *
 * Sending them through `$api` made the auth middleware short-circuit every
 * request for lack of a token, so the tracking page rendered empty and the
 * postal-code step could not even create the tracking key.
 */

export type SaleOrderTrackingAuthenticationIn = components['schemas']['SaleOrderTrackingAuthenticationIn'];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function invalidate(_queryClient: ReturnType<typeof useQueryClient>) {
  //
}

export const useCustomerTrackingDetailsQuery = (key: string) => {
  return $publicApi.useQuery('get', '/customer/tracking/{key}', {
    params: {
      path: {
        key,
      },
    },
  });
};

export const useCompanyLogoQuery = (key: string) => {
  return $publicApi.useQuery('get', '/customer/tracking/{key}/companies/logos/logo/', {
    params: {
      path: {
        key,
      },
    },
  });
};

export const useCarrierLogoQuery = (key: string) => {
  return $publicApi.useQuery('get', '/customer/tracking/{key}/carrier/logos/logo/', {
    params: {
      path: {
        key,
      },
    },
  });
};

export const useProductImageQuery = (key: string, productId: number) => {
  return $publicApi.useQuery('get', '/customer/tracking/{key}/products/{product_id}/images/image/', {
    params: {
      path: {
        key,
        product_id: productId,
      },
    },
  });
};

export const useReturnableProductsQuery = (key: string) => {
  return $publicApi.useQuery('get', '/customer/tracking/{key}/returnable_products', {
    params: {
      path: {
        key,
      },
    },
  });
};

export const usePublicCarrierQuery = (key: string) => {
  return $publicApi.useQuery('get', '/customer/tracking/{key}/carrier', {
    params: {
      path: {
        key,
      },
    },
  });
};

export const useCustomerTrackingKeyCreate = () => {
  const queryClient = useQueryClient();

  const createMutation = $publicApi.useMutation('post', '/customer/tracking/', {
    onSuccess() {
      invalidate(queryClient);
    },
  });

  return createMutation;
};

export const usePublicReturnCreate = () => {
  const queryClient = useQueryClient();

  const createMutation = $publicApi.useMutation('post', '/customer/tracking/{key}/return', {
    onSuccess() {
      invalidate(queryClient);
    },
  });

  return createMutation;
};

export const usePublicChatCreate = () => {
  const queryClient = useQueryClient();

  const createChatMutation = $publicApi.useMutation('post', '/customer/tracking/{key}/chatters/', {
    onSuccess() {
      invalidate(queryClient);
    },
  });

  return createChatMutation;
};
